/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */
import { inject, Injectable } from '@angular/core';
import { SwRegistrationOptions } from '@angular/service-worker';
import { Auth, getAuth } from '@angular/fire/auth';
import { FirebaseApp } from '@angular/fire/app';
import { getMessaging, getToken, onMessage } from '@angular/fire/messaging';
import { ExceptionEntity, ExceptionService } from '@irian-ro/exception-service-module';
import {
  LoggedInUser,
  UserPushNotificationTokenDTO,
  UserPushNotificationTokenResourceService
} from '@irian-ro/auth-shared-module';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EnvironmentService } from '@irian-ro/util-environment';
import { getCircularReplacer } from '@irian-ro/util-functions';

@Injectable({
  providedIn: 'root',
})
export class FirebaseCloudMessagingService {
  private readonly app = inject(FirebaseApp);
  private readonly exceptionService = inject(ExceptionService);
  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly userPushNotificationTokenResourceService = inject(UserPushNotificationTokenResourceService);

  private auth: Auth | undefined = undefined;

  private vapidKey = inject(EnvironmentService).getEnvironmentProperty('deliwellFirebase').vapidKey;

  private swRegistrationOptions = inject(SwRegistrationOptions, { optional: true });

  constructor() {
    this.auth = getAuth(this.app);
  }

  async requestPermissionAndSaveToken(loggedInUser: LoggedInUser) {
    // Never register the service worker when it is disabled for the current
    // environment: a registration on this origin outlives the session and keeps
    // serving cached chunks to every future (dev) build.
    if (!this.swRegistrationOptions?.enabled) {
      return;
    }
    const registration = await navigator.serviceWorker.register('./ngsw-worker.js');

    getToken(getMessaging(), {
      vapidKey: this.vapidKey,
      serviceWorkerRegistration: registration,
    })
      .then((currentToken) => {
        if (currentToken) {
          this.saveUserPushToken(loggedInUser.uuid, currentToken);
        }
      })
      .catch((err) => {
        this.handleErrorRetrievingToken(err);
        this.invalidate(loggedInUser.uuid);
      });
  }

  listenAndShowNotificationsForeground() {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope')?.then((registration) => {
        registration?.showNotification(payload.notification?.title ?? '', payload.notification);
      });
    });
  }

  private handleErrorRetrievingToken(err: any) {
    const exceptionEntity: ExceptionEntity = new ExceptionEntity();
    exceptionEntity.message = 'An error occurred while retrieving token.';
    exceptionEntity.stackTrace = JSON.stringify(err, getCircularReplacer());
    if (err.code != 'messaging/permission-blocked') {
      this.exceptionService.saveException(exceptionEntity).subscribe();
    }
  }

  private saveUserPushToken(userUuid: string | undefined, currentToken: string) {
    const userPushNotificationToken = new UserPushNotificationTokenDTO();
    userPushNotificationToken.token = currentToken;
    userPushNotificationToken.userUuid = userUuid;
    userPushNotificationToken.device = this.deviceDetectorService.deviceType;
    this.userPushNotificationTokenResourceService.save(userPushNotificationToken).subscribe();
  }

  private invalidate(userUuid: string | undefined) {
    this.userPushNotificationTokenResourceService
      .invalidate(userUuid, this.deviceDetectorService.deviceType)
      .subscribe();
  }
}
